import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, MessageCircle, PenLine, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const suggestions = [
  "What services does Kronos offer?",
  "How much does a website cost?",
  "What's your process like?",
];

const leadInputClass =
  "w-full border border-white/10 bg-[#0B0C0E] px-3.5 py-2.5 text-sm text-[#F4F4F6] placeholder-[#3A3E48] outline-none transition-colors focus:border-[#2563EB]";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("kronos-chat-session") || null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", idea: "" });
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking, leadOpen]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.email.trim() || !lead.idea.trim() || leadSubmitting) return;
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: lead.name.trim(),
        email: lead.email.trim(),
        project_type: "Not Sure Yet",
        details: lead.idea.trim(),
        source: "chat",
      });
      setLeadSent(true);
      setLeadOpen(false);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `Thank you, ${lead.name.trim()} — your project is with the studio. Every inquiry is reviewed personally, and a confirmation is on its way to ${lead.email.trim()}.`,
        },
      ]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Something went wrong sending that — please try again, or use the form at /contact." },
      ]);
    } finally {
      setLeadSubmitting(false);
    }
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || thinking) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setThinking(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let firstToken = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line || line === "[DONE]") continue;
          try {
            const data = JSON.parse(line);
            if (data.session_id) {
              setSessionId(data.session_id);
              localStorage.setItem("kronos-chat-session", data.session_id);
            }
            if (data.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: data.error };
                return copy;
              });
            }
            if (data.content) {
              if (firstToken) {
                setThinking(false);
                firstToken = false;
              }
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + data.content,
                };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "The assistant is momentarily unavailable. Please try again.",
        };
        return copy;
      });
    } finally {
      setThinking(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-button"
        aria-label="Open Kronos assistant"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-6 right-6 z-[70] flex h-12 w-12 items-center justify-center rounded-full bg-[#2563EB] text-white shadow-[0_8px_32px_rgba(37,99,235,0.4)] transition-colors hover:bg-[#1D4ED8]"
      >
        {open ? <X size={18} /> : <MessageCircle size={18} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[70] flex h-[520px] w-[calc(100vw-3rem)] max-w-[380px] flex-col overflow-hidden border border-white/10 bg-[#121418]/95 shadow-[0_24px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <img src="/kronos-mark-light.webp" alt="" className="h-6 w-auto" />
              <div>
                <p className="font-display text-sm font-semibold text-[#F4F4F6]">Kronos Assistant</p>
                <p className="text-[11px] text-[#5A5E6B]">Ask about services, process, or pricing</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="chat-messages">
              {messages.length === 0 && (
                <div>
                  <p className="text-sm leading-relaxed text-[#9498A4]">
                    Thinking about a new website? Ask anything — services, process, engagements, or whether a redesign makes sense.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        data-testid={`chat-suggestion-${suggestions.indexOf(s)}`}
                        onClick={() => send(s)}
                        className="rounded-full border border-white/10 px-3.5 py-1.5 text-xs text-[#9498A4] transition-colors hover:border-[#2563EB]/50 hover:text-[#F4F4F6]"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) =>
                m.content === "" && m.role === "assistant" ? null : (
                  <div
                    key={i}
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "ml-auto rounded-2xl rounded-br-sm bg-[#2563EB] text-white"
                        : "mr-auto rounded-2xl rounded-bl-sm bg-white/[0.05] text-[#C0C4CE]"
                    }`}
                  >
                    {m.content}
                  </div>
                )
              )}
              {thinking && (
                <div className="mr-auto flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-white/[0.05] px-4 py-3" data-testid="chat-thinking">
                  {[0, 1, 2].map((d) => (
                    <motion.span
                      key={d}
                      className="h-1.5 w-1.5 rounded-full bg-[#5A5E6B]"
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                    />
                  ))}
                </div>
              )}

              {!leadSent && (
                <div className="pt-2 text-center">
                  <button
                    data-testid="chat-share-project-button"
                    onClick={() => setLeadOpen((o) => !o)}
                    className="link-underline inline-flex items-center gap-2 text-xs font-medium text-[#9498A4] transition-colors hover:text-[#F4F4F6]"
                  >
                    <PenLine size={12} className="text-[#2563EB]" /> Share your project
                  </button>
                </div>
              )}

              <AnimatePresence>
                {leadOpen && !leadSent && (
                  <motion.form
                    data-testid="chat-lead-form"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    onSubmit={submitLead}
                    className="space-y-2.5 overflow-hidden border border-[#2563EB]/30 bg-[#2563EB]/5 p-4"
                  >
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5E6B]">Start your project</p>
                    <input
                      data-testid="chat-lead-name-input"
                      className={leadInputClass}
                      placeholder="Your name"
                      value={lead.name}
                      onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                      required
                    />
                    <input
                      data-testid="chat-lead-email-input"
                      type="email"
                      className={leadInputClass}
                      placeholder="Your email"
                      value={lead.email}
                      onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                      required
                    />
                    <textarea
                      data-testid="chat-lead-idea-textarea"
                      rows={2}
                      className={`${leadInputClass} resize-none`}
                      placeholder="One line about your project"
                      value={lead.idea}
                      onChange={(e) => setLead((l) => ({ ...l, idea: e.target.value }))}
                      required
                    />
                    <button
                      type="submit"
                      data-testid="chat-lead-submit-button"
                      disabled={leadSubmitting}
                      className="w-full rounded-full bg-[#2563EB] py-2.5 text-xs font-medium text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-50"
                    >
                      {leadSubmitting ? "Sending…" : "Send to the studio"}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>

            <form
              className="flex items-center gap-2 border-t border-white/10 p-3"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask a question…"
                className="flex-1 bg-transparent px-3 py-2.5 text-sm text-[#F4F4F6] placeholder-[#3A3E48] outline-none"
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                aria-label="Send message"
                disabled={!input.trim() || thinking}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2563EB] text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-40"
              >
                <ArrowUp size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
